import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Users, Calendar, DollarSign, Edit2, Smartphone, Camera, CheckCircle2, ArrowLeft } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { staffAPI } from '../../services/api';
import { format } from 'date-fns';
import { Badge, AttentionStrip, Button } from '../../components/ui';

const ATT_STATUS_VARIANT = {
  PRESENT: 'success',
  HALF_DAY: 'warning',
  ABSENT: 'danger',
};

const ROLE_LABELS = {
  MANAGER: 'Manager',
  OPERATOR: 'Operator',
  GROUNDSMAN: 'Ground keeper',
  CLEANER: 'Cleaner',
  PITCH_CURATOR: 'Pitch curator',
  ELECTRICIAN: 'Electrician',
};

const INVITE_LABELS = {
  NOT_INVITED: 'No app access',
  INVITED: 'Invite pending',
  ACTIVE: 'Can sign in',
};

export default function StaffDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('overview');
  const [attendance, setAttendance] = useState([]);
  const [salary, setSalary] = useState([]);
  const [showCreatedBanner, setShowCreatedBanner] = useState(Boolean(location.state?.created));

  useEffect(() => {
    if (!id) return;
    staffAPI.getStats(id).then((res) => setStats(res.data)).catch(() => navigate('/dashboard/staff'));
  }, [id, navigate]);

  useEffect(() => {
    if (tab === 'attendance' && id) {
      staffAPI.getAttendance(id).then((res) => setAttendance(res.data || []));
    }
    if (tab === 'salary' && id) {
      staffAPI.getSalary(id).then((res) => setSalary(res.data || []));
    }
  }, [tab, id]);

  if (!stats) return <DashboardLayout><div className="p-8 text-ga-text-muted">Loading...</div></DashboardLayout>;

  const hasAppAccess = Boolean(stats.user_id);
  const inviteStatus = (stats.invite_status || 'NOT_INVITED').toUpperCase();
  const createdWithAppAccess = location.state?.withAppAccess;
  const createdPhone = location.state?.phone || stats.linked_user_phone || stats.phone;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: Users },
    { key: 'attendance', label: 'Attendance', icon: Calendar },
    { key: 'salary', label: 'Salary History', icon: DollarSign },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {showCreatedBanner && (
          <AttentionStrip
            tone="primary"
            icon={CheckCircle2}
            title="Employee created"
            message={
              createdWithAppAccess
                ? `${stats.name} can sign in with OTP on ${createdPhone || 'their mobile'}. Next: register their face for selfie check-in.`
                : `${stats.name} was added to your team. Grant app access from Edit if they need phone login.`
            }
            action={hasAppAccess ? (
              <Button
                as={Link}
                to={`/dashboard/selfie-attendance/register-face/${id}`}
                variant="secondary"
                className="px-3 py-1.5 text-xs whitespace-nowrap"
              >
                Register face
              </Button>
            ) : null}
          />
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to="/dashboard/staff"
              aria-label="Back to staff list"
              className="shrink-0 p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-ga-text-primary truncate">{stats.name}</h1>
              <p className="text-ga-text-muted truncate">
                {ROLE_LABELS[(stats.role || '').toUpperCase()] || stats.role} • {stats.phone || '—'}
              </p>
            </div>
          </div>
          <Link
            to={`/dashboard/staff/${id}/edit`}
            className="inline-flex items-center gap-2 ga-btn-ghost px-4 py-2 rounded-lg"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </Link>
        </div>

        <div className="flex gap-2 border-b border-ga-border-subtle">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => {
                setShowCreatedBanner(false);
                setTab(t.key);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${tab === t.key ? 'bg-ga-bg-surface border border-ga-border-subtle border-b-0 text-ga-text-primary' : 'text-ga-text-muted hover:text-ga-text-primary'}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="ga-card rounded-xl shadow-sm border p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-ga-primary" />
                  <h2 className="font-semibold text-ga-text-primary">App access</h2>
                </div>
                <Badge variant={hasAppAccess ? 'success' : 'neutral'}>
                  {INVITE_LABELS[inviteStatus] || inviteStatus}
                </Badge>
              </div>
              {hasAppAccess ? (
                <div className="space-y-3 text-sm">
                  <p className="text-ga-text-muted">
                    Login username: <span className="text-ga-text-primary font-medium">{stats.linked_username || '—'}</span>
                    {' • '}
                    OTP phone: <span className="text-ga-text-primary font-medium">{stats.linked_user_phone || stats.phone || '—'}</span>
                  </p>
                  <p className="text-ga-text-muted">
                    Ask them to open the app, enter their mobile number, and verify with the OTP code.
                  </p>
                  <Button
                    as={Link}
                    to={`/dashboard/selfie-attendance/register-face/${id}`}
                    variant="secondary"
                    className="inline-flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" />
                    Register face for selfie check-in
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-ga-text-muted">
                  This employee does not have a login yet. Edit their profile and enable app access when they need phone OTP sign-in.
                </p>
              )}
            </div>

            <div className="ga-card rounded-xl shadow-sm border p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-ga-bg-page/50">
                  <p className="text-sm text-ga-text-muted">Total working days</p>
                  <p className="text-xl font-semibold text-ga-text-primary">{stats.total_working_days ?? 0}</p>
                </div>
                <div className="p-4 rounded-lg bg-ga-bg-page/50">
                  <p className="text-sm text-ga-text-muted">Monthly salary</p>
                  <p className="text-xl font-semibold text-ga-text-primary">₹{Number(stats.monthly_salary || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 rounded-lg bg-ga-bg-page/50">
                  <p className="text-sm text-ga-text-muted">Last payment</p>
                  <p className="text-xl font-semibold text-ga-text-primary">
                    {stats.last_payment?.payment_date ? format(new Date(stats.last_payment.payment_date), 'dd MMM yyyy') : '—'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-ga-bg-page/50">
                  <p className="text-sm text-ga-text-muted">Attendance %</p>
                  <p className="text-xl font-semibold text-ga-text-primary">{stats.attendance_percentage ?? 0}%</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-ga-border-subtle text-sm text-ga-text-muted">
                Email: {stats.email || '—'} • Joining: {stats.joining_date ? format(new Date(stats.joining_date), 'dd MMM yyyy') : '—'} • Status: {stats.status}
              </div>
            </div>
          </div>
        )}

        {tab === 'attendance' && (
          <div className="ga-card rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ga-border-subtle text-left text-ga-text-muted">
                    <th className="p-3 font-medium">Date</th>
                    <th className="p-3 font-medium">Check-in</th>
                    <th className="p-3 font-medium">Check-out</th>
                    <th className="p-3 font-medium">Hours</th>
                    <th className="p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((row) => (
                    <tr key={row.id} className="border-b border-ga-border-subtle/50">
                      <td className="p-3">{row.attendance_date ? format(new Date(row.attendance_date), 'dd MMM yyyy') : '—'}</td>
                      <td className="p-3">{row.check_in_time || '—'}</td>
                      <td className="p-3">{row.check_out_time || '—'}</td>
                      <td className="p-3">{row.total_hours != null ? Number(row.total_hours).toFixed(1) : '—'}</td>
                      <td className="p-3">
                        <Badge variant={ATT_STATUS_VARIANT[row.status] || 'neutral'}>{row.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {attendance.length === 0 && <div className="p-8 text-center text-ga-text-muted">No attendance records.</div>}
            </div>
          </div>
        )}

        {tab === 'salary' && (
          <div className="ga-card rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ga-border-subtle text-left text-ga-text-muted">
                    <th className="p-3 font-medium">Month</th>
                    <th className="p-3 font-medium">Base</th>
                    <th className="p-3 font-medium">Extra</th>
                    <th className="p-3 font-medium">Deductions</th>
                    <th className="p-3 font-medium">Final</th>
                    <th className="p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {salary.map((row) => (
                    <tr key={row.id} className="border-b border-ga-border-subtle/50">
                      <td className="p-3">{row.salary_year}-{String(row.salary_month).padStart(2, '0')}</td>
                      <td className="p-3">₹{Number(row.base_salary).toLocaleString()}</td>
                      <td className="p-3">₹{Number(row.extra_pay || 0).toLocaleString()}</td>
                      <td className="p-3">₹{Number(row.deductions || 0).toLocaleString()}</td>
                      <td className="p-3 font-medium">₹{Number(row.final_salary).toLocaleString()}</td>
                      <td className="p-3">
                        <span className={row.payment_status === 'PAID' ? 'text-ga-success' : 'text-ga-warning'}>{row.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {salary.length === 0 && <div className="p-8 text-center text-ga-text-muted">No salary records.</div>}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
